import { useEffect, useState } from 'react'

import { Grid } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'
import { useMaterialUIController } from 'context'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import ManageCard from 'modules/ManageCard'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { ROUTE_PATHS } from 'routes'

import MainForm from './components/MainForm'
import {
	defaultSpecialLocationsForm as defaultSpecialLocationsForm,
	SpecialLocationsFormContext,
} from './constants'
import { SpecialLocationsForm } from '../types'
import { postNewSpecialLocations } from '../utils'

function ManageSpecialLocationsForm() {
	const [controller] = useMaterialUIController()
	const { session } = controller

	const [specialLocationsForm, setSpecialLocationsForm] = useState(defaultSpecialLocationsForm)
	const [formEdited, setFormEdited] = useState<boolean>(false)
	const [showError, setShowError] = useState<boolean>(false)
	const navigate = useNavigate()

	const location = useLocation()
	const locationPathNameParts = location.pathname.split('/')
	const id = locationPathNameParts[locationPathNameParts.length - 1]
	const isNew = id === 'new'

	const fetchData = async () => {
		const response = await axios.get<SpecialLocationsForm>(
			`${process.env.REACT_APP_API_URL}/special-locations/item`,
			{
				params: { id },
				headers: {
					Authorization: await session.user.getIdToken(),
				},
			}
		)
		return response.data
	}

	const deleteItem = async () => {
		const response = await axios.delete<SpecialLocationsForm>(
			`${process.env.REACT_APP_API_URL}/special-locations/item`,
			{
				params: { id },
				headers: {
					Authorization: await session.user.getIdToken(),
				},
			}
		)
		return response.data
	}

	const { data, isLoading, error } = useQuery({
		queryKey: ['special-locations-item', id],
		queryFn: fetchData,
		enabled: !isNew,
	})

	useEffect(() => {
		setSpecialLocationsForm(
			data || {
				...defaultSpecialLocationsForm,
				geometry: {
					lat: session.data.organisation.default_location.geometry.lat,
					lng: session.data.organisation.default_location.geometry.lng,
				},
			}
		)
	}, [data, isNew])

	return (
		<SpecialLocationsFormContext.Provider
			value={{
				specialLocationsForm,
				setSpecialLocationsForm,
				formEdited,
				setFormEdited,
				showError,
				setShowError,
			}}
		>
			<DashboardLayout>
				<DashboardNavbar isMini />
				<MDBox mt={4}>
					<Grid container spacing={3}>
						<Grid item xs={12} lg={9}>
							<MDBox mb={3}>
								{isNew || (!isLoading && !error) ? <MainForm /> : <LoadingComponent type="box" />}
							</MDBox>
						</Grid>
						<Grid item xs={12} lg={3}>
							<Grid container spacing={3}>
								<Grid item xs={12}>
									<ManageCard
										formEdited={formEdited}
										setFormEdited={setFormEdited}
										save={{
											onClicked: async () => {
												const trimmedNames = specialLocationsForm.names.map(name => name.trim())
												const hasNoHyphens = trimmedNames.every(name => !name.includes('-'))
												const hasAtLeastOneNonEmpty = trimmedNames.some(name => name !== '')

												if (hasNoHyphens && hasAtLeastOneNonEmpty) {
													await postNewSpecialLocations(session.user, [specialLocationsForm])
													// TODO: Show erorr message for duplicates in list if errors
												} else {
													setShowError(true)
													throw new Error('Invalid names format they cannot contain hyphens')
												}
											},
										}}
										deleteItem={{
											visible: !isNew,
											onClicked: async () => {
												await deleteItem()
												navigate(ROUTE_PATHS.SPECIAL_LOCATIONS.BASE)
											},
										}}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</MDBox>
			</DashboardLayout>
		</SpecialLocationsFormContext.Provider>
	)
}

export default ManageSpecialLocationsForm
